import type { DebouncedFunc } from 'lodash'
import { debounce } from 'lodash/fp'
import { LRUCache } from 'lru-cache'
import { reaction } from 'mobx'

import { DisposeBag } from '@src/lib/DisposeBag/DisposeBag'

import type Service from '.'
import Collection from './collections/Collection'
import type { ContactModel } from './model/contact/ContactModel'
import type {
  ContactSuggestionStatus,
  RawContactSuggestion,
  SuggestedFields,
} from './model/contact/ContactSuggestionModel'
import { ContactSuggestionModel } from './model/contact/ContactSuggestionModel'

/**
 * Max number of contact suggestions to store, overflowed items get discarded.
 */
const MAX_COLLECTION_SIZE = 100

const DEBOUNCED_FETCH_MISSING_WAIT = 200

const OBSERVERS_TTL = 1000 * 60 * 5

export default class ContactSuggestionStore {
  readonly collection: Collection<ContactSuggestionModel>

  private readonly disposeBag = new DisposeBag()

  /**
   * Keeps track of requested contact suggestions
   */
  private observedContactSuggestionsByPhoneNumber = new LRUCache<string, boolean>({
    max: MAX_COLLECTION_SIZE,
    ttl: OBSERVERS_TTL,
    disposeAfter: (_, phoneNumber) => {
      this.collection.delete(phoneNumber)
      this.fetchedContactSuggestions.delete(phoneNumber)
    },
  })

  /**
   * Observers will trigger requests, because there can be many observers
   * the request is debounced so that after observers get rendered the
   * data gets requested.
   */
  private debouncedFetchObserved: DebouncedFunc<() => Promise<void>>

  /**
   * Keeps track of recently fetched contact suggestions to not fetch
   * them again. The entries from the observed contact suggestions lru
   * that get disposed will delete entries from this set which will
   * allow users fetch suggestions again.
   */
  private fetchedContactSuggestions = new Set<string>()

  constructor(private root: Service) {
    this.collection = new Collection<ContactSuggestionModel>({
      idKey: 'phoneNumber',
      maxSize: MAX_COLLECTION_SIZE,
    })

    this.debouncedFetchObserved = debounce(DEBOUNCED_FETCH_MISSING_WAIT)(
      this.fetchObserved.bind(this),
    )

    this.disposeBag.add(this.subscribeToWebSocket())
    this.disposeBag.add(this.watchSuggestionsEnabled())
  }

  private async fetchByPhoneNumbers(phoneNumbers: string[]) {
    const { data } = await this.root.transport.contacts.suggestions
      .for(this.root.organization.getCurrentOrganization().id)
      .fetchByPhoneNumbers({ phoneNumbers })

    const contactSuggestionModels = data.map((raw) => new ContactSuggestionModel(raw))

    this.collection.putBulk(contactSuggestionModels)
  }

  private async fetchObserved() {
    const missingObservedContactSuggestions = [
      ...this.observedContactSuggestionsByPhoneNumber.keys(),
    ].filter(
      (number) =>
        !this.collection.has(number) && !this.fetchedContactSuggestions.has(number),
    )

    if (!missingObservedContactSuggestions.length) {
      return
    }

    for (const contactSuggestionToFetch of missingObservedContactSuggestions) {
      this.fetchedContactSuggestions.add(contactSuggestionToFetch)
    }

    await this.fetchByPhoneNumbers(missingObservedContactSuggestions)
  }

  getByNumber(number: string): ContactSuggestionModel | null {
    this.observedContactSuggestionsByPhoneNumber.set(number, true)

    setTimeout(() => void this.debouncedFetchObserved())

    const suggestion = this.collection.get(number)

    if (suggestion?.status !== 'pending') {
      return null
    }

    return suggestion
  }

  getByContact(contact: ContactModel): ContactSuggestionModel | null {
    if (!contact.local) {
      return null
    }

    const number = contact.phoneNumbers[0]?.getTypeSafeValue()
    if (!number) {
      return null
    }

    return this.getByNumber(number)
  }

  getMergedSuggestedFields(
    contact: ContactModel,
    contactSuggestion: ContactSuggestionModel,
  ) {
    return {
      firstName: contact.firstName ?? contactSuggestion.suggestedFields.firstName,
      lastName: contact.lastName ?? contactSuggestion.suggestedFields.lastName,
      role: contact.role ?? contactSuggestion.suggestedFields.role,
      company: contact.company ?? contactSuggestion.suggestedFields.company,
    }
  }

  async update(
    contactSuggestion: ContactSuggestionModel,
    status: ContactSuggestionStatus,
    suggestedFields: SuggestedFields,
  ) {
    contactSuggestion.localUpdate({ status })

    return this.root.transport.contacts.suggestions.update(
      contactSuggestion.id,
      status,
      suggestedFields,
    )
  }

  async markNotSpam(phoneNumber: string): Promise<void> {
    await this.root.transport.contacts.suggestions.markNotSpam(phoneNumber)
  }

  toggleContactSuggestions = (enabled: boolean) => {
    return this.root.transport.contacts.settings.put({ suggestionsEnabled: enabled })
  }

  private putRawContactSuggestionToCollection(
    rawContactSuggestion: RawContactSuggestion | null,
  ) {
    if (!rawContactSuggestion) {
      return
    }

    if (
      !this.observedContactSuggestionsByPhoneNumber.has(rawContactSuggestion.phoneNumber)
    ) {
      return
    }

    const contactSuggestion = new ContactSuggestionModel(rawContactSuggestion)
    this.collection.put(contactSuggestion)
  }

  private deleteContactSuggestion(suggestionId: string) {
    const foundContactSuggestion = this.collection.find(
      (suggestion) => suggestion.id === suggestionId,
    )
    if (!foundContactSuggestion) {
      return
    }
    this.collection.delete(foundContactSuggestion)
  }

  private clearAllSuggestions() {
    this.collection.clear()
    this.fetchedContactSuggestions.clear()
    this.observedContactSuggestionsByPhoneNumber.clear()
  }

  private watchSuggestionsEnabled() {
    // Reacts to suggestionsEnabled going true->false to clear the suggestion cache (MDR-1982),
    // since the backend no longer sends a per-row delete notification. Disposed via disposeBag.
    return reaction(
      () => this.root.contact.sharedContactSettings?.suggestionsEnabled,
      (enabled, wasEnabled) => {
        if (wasEnabled && !enabled) {
          this.clearAllSuggestions()
        }
      },
    )
  }

  private subscribeToWebSocket() {
    return this.root.transport.onNotificationData.subscribe((data) => {
      switch (data.type) {
        case 'contact-suggestion-create':
        case 'contact-suggestion-update': {
          this.putRawContactSuggestionToCollection(data.contactSuggestion)
          break
        }
        case 'contact-suggestion-delete': {
          this.deleteContactSuggestion(data.contactSuggestionId)
          break
        }
        case 'contact-suggestion-update-error': {
          this.putRawContactSuggestionToCollection(data.validData)
          break
        }
      }
    })
  }
}
